import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { 
  FiCheckCircle, 
  FiAward, 
  FiTarget, 
  FiUsers, 
  FiCompass, 
  FiArrowRight, 
  FiStar, 
  FiLayers, 
  FiHeart 
} from "react-icons/fi";
import "./about.css";

const STATS = [
  { label: "Years of Excellence", value: "20+" },
  { label: "Curated Artisans & Vendors", value: "500+" },
  { label: "Festival Attendees", value: "100k+" },
  { label: "Live Experiences Produced", value: "50+" },
];

const SERVICES = [
  {
    icon: <FiStar />,
    title: "Concept & Creative Direction",
    description: "Designing distinct event identities that blend African craftsmanship, contemporary art, and culture."
  },
  {
    icon: <FiUsers />,
    title: "Curated Artisan Markets",
    description: "Connecting 200+ vetted local makers, ethical fashion brands, and food entrepreneurs with audiences."
  },
  {
    icon: <FiLayers />,
    title: "End-to-End Production",
    description: "Flawless site logistics, vendor placement, security, stage sound, and on-site experience management."
  },
  {
    icon: <FiHeart />,
    title: "Audience & Family Engagement",
    description: "Creating inclusive grounds with dedicated kids zones, live acoustic stages, gourmet courts, and pet-friendly spaces."
  }
];

const About = () => {
  return (
    <>
      <Helmet>
        <title>About Us | BizBaz Events Nairobi</title>
        <meta
          name="description"
          content="Learn about BizBaz Events — Nairobi's premier event organizers behind Bizarre Bazaar. Nearly 20 years of creating immersive cultural festivals, craft markets, and live experiences."
        />
        <meta property="og:title" content="About BizBaz Events | Where Cultures Connect" />
        <meta
          property="og:description"
          content="Over 20 years of designing thoughtfully curated events across Kenya. Discover our story, team, and vision."
        />
        <link rel="canonical" href="https://bizbazevents.com/about" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "BizBaz Events",
            "url": "https://bizbazevents.com",
            "logo": "https://bizbazevents.com/logo.jpg",
            "description": "Nairobi's premier event management company specializing in artisan bazaars, cultural festivals, and live experiences.",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Nairobi",
              "addressCountry": "KE"
            }
          })}
        </script>
      </Helmet>

      <main className="about-page">
        {/* HERO */}
        <section className="about-hero">
          <span className="about-eyebrow">
            <FiCompass className="eyebrow-icon" /> OUR STORY &amp; PURPOSE
          </span>
          <h1 className="about-title">WHERE CULTURES &amp; CREATIVITY CONNECT</h1>
          <p className="about-lead">
            For nearly two decades, BizBaz Events has been designing, curating, and producing immersive festivals that celebrate African innovation, craftsmanship, and community across Nairobi.
          </p>
        </section>

        {/* STATS */}
        <section className="about-stats-grid">
          {STATS.map((stat, i) => (
            <div className="stat-card" key={i}>
              <strong className="stat-value">{stat.value}</strong>
              <span className="stat-label">{stat.label}</span>
            </div>
          ))}
        </section>

        {/* WHO WE ARE */}
        <section className="about-grid-section">
          <div className="about-text-content">
            <span className="section-tag">WHO WE ARE</span>
            <h2>More Than Event Organizers — We Are Experience Architects.</h2>
            <p>
              BizBaz Events specializes in creating thoughtfully curated gatherings that go far beyond basic event logistics. We build living ecosystems where local artisans, creative entrepreneurs, food innovators, and performing artists seamlessly connect with thousands of attendees.
            </p>
            <p>
              Whether it's the iconic <strong>Bizarre Bazaar Global Village Edition</strong> or our <strong>Christmas Craft Market</strong>, every project balances creative ambition with military-grade operational precision.
            </p>
          </div>

          <div className="about-image-card">
            <img 
              src="/images/REM00008.jpg" 
              alt="BizBaz Festival Crowd" 
              className="about-feature-img"
              onError={(e) => {
                e.target.src = "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?auto=format&fit=crop&w=800&q=80";
              }}
            />
            <div className="img-card-caption">
              <span>Bizarre Bazaar Festival Grounds — Gigiri, Nairobi</span>
            </div>
          </div>
        </section>

        {/* SERVICES */}
        <section className="about-services-section">
          <div className="section-header-center">
            <span className="section-tag">OUR CAPABILITIES</span>
            <h2>WHAT WE DO</h2>
            <p>From strategic concept design to on-the-ground execution.</p>
          </div>

          <div className="services-grid">
            {SERVICES.map((s, idx) => (
              <div className="service-card" key={idx}>
                <div className="service-icon">{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* WHY CHOOSE US */}
        <section className="about-pillars-section">
          <span className="section-tag">THE BIZBAZ STANDARD</span>
          <h2>WHY BRANDS &amp; ARTISANS TRUST US</h2>

          <div className="pillars-grid">
            <div className="pillar-item">
              <div className="pillar-header">
                <FiAward className="pillar-icon" />
                <h3>Proven Legacy</h3>
              </div>
              <p>Two decades of consistent, high-yield festivals with established safety, vendor support, and crowd-drawing power.</p>
            </div>

            <div className="pillar-item">
              <div className="pillar-header">
                <FiTarget className="pillar-icon" />
                <h3>Curated Excellence</h3>
              </div>
              <p>We strictly vet vendors and performers to maintain high aesthetic standards, avoiding mass-produced clutter.</p>
            </div>

            <div className="pillar-item">
              <div className="pillar-header">
                <FiCheckCircle className="pillar-icon" />
                <h3>Seamless Operations</h3>
              </div>
              <p>Handling complex site setups, traffic flow, M-Pesa ticketing integration, security, and venue compliance smoothly.</p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="about-cta-banner">
          <div className="cta-inner">
            <h2>READY TO EXHIBIT OR PARTNER WITH US?</h2>
            <p>Join hundreds of local brands reaching thousands of engaged attendees at our next festival edition.</p>
            <div className="cta-actions">
              <Link to="/contact" className="about-btn primary-btn">
                EXHIBIT WITH US <FiArrowRight />
              </Link>
              <Link to="/events" className="about-btn secondary-btn">
                EXPLORE UPCOMING EVENTS
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default About;